import { useState, useEffect, useCallback } from 'react';

interface ScrollState {
  scrollY: number;
  isScrolled: boolean;
  currentSection: string;
  scrollDirection: 'up' | 'down';
}

interface SectionElement {
  id: string;
  element: HTMLElement;
  isRevealed: boolean;
}

export const useScrollManager = () => {
  const [scrollState, setScrollState] = useState<ScrollState>({
    scrollY: 0,
    isScrolled: false,
    currentSection: 'hero',
    scrollDirection: 'down'
  });

  const [sections, setSections] = useState<SectionElement[]>([]);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Register sections for scroll-triggered reveals
  const registerSection = useCallback((id: string, element: HTMLElement) => {
    setSections(prev => {
      const existing = prev.find(s => s.id === id);
      if (existing) return prev;
      return [...prev, { id, element, isRevealed: false }];
    });
  }, []);

  // Handle scroll-triggered section reveals
  const handleSectionReveals = useCallback(() => {
    sections.forEach(section => {
      const rect = section.element.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      // Trigger reveal when section is 20% visible from bottom
      const shouldReveal = rect.top <= viewportHeight * 0.8;
      
      if (shouldReveal && !section.isRevealed) {
        section.element.classList.add('section-revealed');
        section.isRevealed = true;
        
        // Add slide-up animation
        section.element.style.transform = 'translateY(0)';
        section.element.style.opacity = '1';
      }
    });
  }, [sections]);

  // Main scroll handler
  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    const direction = currentScrollY > lastScrollY ? 'down' : 'up';
    
    setScrollState(prev => ({
      ...prev,
      scrollY: currentScrollY,
      isScrolled: currentScrollY > 50,
      scrollDirection: direction
    }));
    
    setLastScrollY(currentScrollY);
    handleSectionReveals();
  }, [lastScrollY, handleSectionReveals]);


  // Initialize scroll listeners
  useEffect(() => {
    // Throttled scroll handler for performance
    let ticking = false;
    
    const throttledScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledScroll, { passive: true });
    
    // Initial setup for sections
    const initializeSections = () => {
      const sectionElements = document.querySelectorAll('[data-scroll-section]');
      sectionElements.forEach(el => {
        const element = el as HTMLElement;
        const id = element.dataset.scrollSection || '';
        if (id) {
          registerSection(id, element);
          
                  // Initial state: sections start visible but can be animated
        element.style.transform = 'translateY(0)';
        element.style.opacity = '1';
        element.style.transition = 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
        }
      });
    };

    // Run after DOM is ready
    setTimeout(initializeSections, 100);
    
    return () => {
      window.removeEventListener('scroll', throttledScroll);
    };
  }, [handleScroll, registerSection]);

  return {
    scrollState,
    registerSection
  };
};
